import { Suspense, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDatabasesNames } from "services/databases/useDatabasesNames";
import { Role } from "services/rbac/types";

import DatabaseIcon from "assets/icons/DatabaseSelectorIcon.svg?react";

import { EmptyDatabasesList } from "pages/DevelopWorkspace/Editor/Document/DocumentContextSelector/EmptyDatabasesList/EmptyDatabasesList";
import { NONE } from "pages/DevelopWorkspace/Editor/Document/DocumentContextSelector/types";

import ContextMenuItem from "components/ContextMenu/ContextMenuItem";
import LoadingOverlap from "components/LoadingOverlap";
import { Step } from "components/Wizard/Step/Step";
import { ContextSelector } from "components/composite/ContextSelector/ContextSelector";

import {
  ROLES_WIZARD_INIT_EDIT_DATABASE_PRIVILEGES,
  ROLES_WIZARD_SET_EMPTY_SECTION_ERRORS,
  hasEmptySectionErrors,
} from "../privilegesReducer";
import { PrivilegesStepType } from "../types";
import { usePrivilegesState } from "../usePrivilegesContext";
import { DatabasePrivileges } from "./DatabasePrivileges";

import styles from "./styles.module.scss";

type Props = {
  onClose: () => void;
  onSubmit: (data: PrivilegesStepType) => void;
  onPrevStep: () => void;
  activeStepIndex: number;
  role?: Role;
};

export const EditDatabasePrivilegesStep = (props: Props) => {
  const { onClose, activeStepIndex, onPrevStep, onSubmit, role } = props;
  const { t } = useTranslation();
  const databases = useDatabasesNames();

  const databaseSelectorRef = useRef<HTMLDivElement>(null);
  const [databaseSelectorOpen, setDatabaseSelectorOpen] = useState(false);
  const [searchDatabase, setSearchDatabase] = useState("");
  const {
    privilegesState,
    privilegesDispatch,
    getDiff,
    validateEmptySections,
  } = usePrivilegesState();
  const [currentDatabase, setCurrentDatabase] = useState<string | typeof NONE>(
    NONE
  );

  const hasPrivileges =
    currentDatabase &&
    currentDatabase !== NONE &&
    privilegesState.databasePrivileges[currentDatabase];

  useEffect(() => {
    if (hasPrivileges) {
      privilegesDispatch({
        type: ROLES_WIZARD_INIT_EDIT_DATABASE_PRIVILEGES,
        catalogName: currentDatabase,
      });
    }
  }, [currentDatabase, privilegesDispatch, hasPrivileges]);

  const foundDatabases = databases.filter(database => {
    return database.catalogName
      .toLowerCase()
      .includes(searchDatabase.toLowerCase());
  });

  const getDatabaseNameLabel = (databaseName: string | Symbol) => {
    if (!databaseName || databaseName === NONE) {
      return t("workspace.context_selector.no_db_selected");
    }
    return databaseName as string;
  };

  const dbItems = [
    ...foundDatabases.map(database => (
      <ContextMenuItem
        key={database.catalogName}
        checked={database.catalogName === currentDatabase}
        onClick={() => {
          setCurrentDatabase(database.catalogName);
          setDatabaseSelectorOpen(false);
        }}
        text={database.catalogName}
        testId={`context-db-item-${database.catalogName}`}
      />
    )),
  ];

  if (!searchDatabase) {
    dbItems.unshift(
      <ContextMenuItem
        text={t("workspace.context_selector.none")}
        key="none"
        testId="no-database-item"
        onClick={() => {
          setCurrentDatabase(NONE);
          setDatabaseSelectorOpen(false);
        }}
      />
    );
  }

  const body = (
    <>
      <div className={styles.row}>
        <div className={styles.label}>
          {t("roles_wizard.database_privileges.select_database")}
        </div>
        <ContextSelector
          isDisabled={false}
          active={!!currentDatabase}
          selectorRef={databaseSelectorRef}
          onClick={() => {
            setSearchDatabase("");
            setDatabaseSelectorOpen(true);
          }}
          testId="roles-wizard-database-context-selector"
          leftIcon={<DatabaseIcon />}
          label={getDatabaseNameLabel(currentDatabase)}
          selectorIsOpen={databaseSelectorOpen}
          onClose={() => {
            setDatabaseSelectorOpen(false);
          }}
          showSearch={databases.length > 5}
          setSearchValue={setSearchDatabase}
          searchValue={searchDatabase}
          searchTestId="search-database-context"
          notFoundText={t("workspace.context_selector.no_matching_db")}
          items={dbItems}
          noItemsFallback={<EmptyDatabasesList />}
          noSearchResults={foundDatabases.length === 0}
        />
      </div>
      {role && currentDatabase !== NONE && (
        <Suspense fallback={<LoadingOverlap isLoading />}>
          <DatabasePrivileges
            catalogName={currentDatabase}
            role={role}
          />
        </Suspense>
      )}
    </>
  );

  const onFormSubmit = () => {
    const submit = {
      privileges: privilegesState,
    };
    const errors = validateEmptySections({
      catalogName: currentDatabase as string,
    });
    if (hasEmptySectionErrors(errors)) {
      privilegesDispatch({
        type: ROLES_WIZARD_SET_EMPTY_SECTION_ERRORS,
        errors,
      });

      return;
    }
    if (role) {
      const diff = getDiff(role);
      Object.assign(submit, { diff });
    }
    onSubmit(submit);
  };

  return (
    <Step
      title={t("roles_wizard.database_privileges.title")}
      subtitle={t("roles_wizard.database_privileges.subtitle")}
      body={body}
      onClose={onClose}
      onSubmit={onFormSubmit}
      activeStepIndex={activeStepIndex}
      onPrevStep={onPrevStep}
      titleTestId="roles-wizard-privileges-title"
    />
  );
};
